// src/api/promoClient.ts

export interface PromoData {
  code: string
  discountPercent: number
  freeMonths: number
  appliedAt: string
  source: string
}

const API_URL = import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api'

/**
 * Save promo code data to the backend for a specific user
 */
export async function savePromoToBackend(userId: string, promoData: PromoData): Promise<{ success: boolean }> {
  try {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token')
    
    console.log('📤 Saving promo to backend:', { userId, promoData })
    
    const response = await fetch(`${API_URL}/promo/apply`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        userId,
        code: promoData.code,
        discountPercent: promoData.discountPercent,
        freeMonths: promoData.freeMonths,
        appliedAt: promoData.appliedAt,
        source: promoData.source
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to save promo to backend')
    }

    const data = await response.json()
    console.log('✅ Promo saved successfully:', data)
    return data
  } catch (error) {
    console.error('❌ Failed to save promo to backend:', error)
    throw error
  }
}

/**
 * Validate a promo code (check if it's valid without applying it)
 */
export async function validatePromoCode(code: string): Promise<{
  valid: boolean
  discountPercent: number
  freeMonths: number
  message: string
}> {
  try {
    const response = await fetch(`${API_URL}/promo/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ code })
    })

    const data = await response.json()
    return data
  } catch (error) {
    console.error('❌ Failed to validate promo:', error)
    return {
      valid: false,
      discountPercent: 0,
      freeMonths: 0,
      message: 'Failed to validate promo code'
    }
  }
}

/**
 * Get active promo for current user
 */
export async function getActivePromo(): Promise<PromoData | null> {
  try {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token')
    
    const response = await fetch(`${API_URL}/promo/active`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      return null
    }

    const data = await response.json()
    return data.promo || null
  } catch (error) {
    console.error('❌ Failed to get active promo:', error)
    return null
  }
} 